import { Env } from '@env';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useMemo, useState } from 'react';
import {
  Keyboard,
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { useCreateLocation } from '@/api/partners/locations/use-create-location';
import type { Address } from '@/api/subscriptions/types';
import { clearByPrefix, usePersistedState } from '@/core/storage';
import { Button, Card, Input, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import GoogleAutocomplete from '@/ui/google-autocomplete';
import { Icon } from '@/ui/icon';
import { ArrowRight, Search } from '@/ui/icons';
import { InputMulti } from '@/ui/input-multi';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

export default function AddLocation() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();

  const draftPrefix = 'owner-add-location-draft:';
  const k = (name: string) => `${draftPrefix}${name}`;

  const [address, setAddress] = usePersistedState<Address | null>(
    k('address'),
    null
  );
  const [pickupInstructions, setPickupInstructions] = usePersistedState<string>(
    k('pickupInstructions'),
    ''
  );
  const [dropoffInstructions, setDropoffInstructions] =
    usePersistedState<string>(k('dropoffInstructions'), '');
  const [showGoogleAutoComplete, setShowGoogleAutoComplete] =
    useState<boolean>(false);
  const [enableGarageHours, setEnableGarageHours] = usePersistedState<boolean>(
    k('enableGarageHours'),
    false
  );
  const [garageOpenHour, setGarageOpenHour] = usePersistedState<string>(
    k('garageOpenHour'),
    ''
  );
  const [garageCloseHour, setGarageCloseHour] = usePersistedState<string>(
    k('garageCloseHour'),
    ''
  );

  const { mutateAsync: createLocation, isPending: isCreatingLocation } =
    useCreateLocation();

  const queryClient = useQueryClient();

  const options = useMemo(() => {
    return Array.from({ length: 24 * 2 }, (_, i) => {
      const hour = Math.floor(i / 2);
      const minute = (i % 2) * 30;
      const pad = (n: number) => n.toString().padStart(2, '0');
      return {
        id: `${pad(hour)}:${pad(minute)}`,
        value: `${pad(hour)}:${pad(minute)}`,
        title:
          hour === 0
            ? `12:${pad(minute)} AM`
            : hour < 12
            ? `${pad(hour)}:${pad(minute)} AM`
            : hour === 12
            ? `12:${pad(minute)} PM`
            : `${pad(hour - 12)}:${pad(minute)} PM`,
      };
    });
  }, []);

  const getTimeZone = async ({ lat, lng }: any) => {
    const now = Date.now() / 1000;
    const key = Env.GOOGLE_TIMEZONE_API_KEY;

    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat}%2C${lng}&timestamp=${now}&key=${key}`;

    const res = await fetch(url, {
      method: 'GET',
    });
    const d = await res.json();

    return d;
  };

  const handleAddLocation = async () => {
    const timeZone = await getTimeZone({
      lat: address?.latitude,
      lng: address?.longitude,
    });

    try {
      await createLocation({
        address: address!.description,
        zipcode: address!.zipcode,
        city: address!.city,
        state: address!.state,
        latitude: address!.latitude,
        longitude: address!.longitude,
        pickupInstructions,
        dropoffInstructions,
        timeZone: timeZone.timeZoneId,
        garageOpenHour: enableGarageHours ? garageOpenHour : undefined,
        garageCloseHour: enableGarageHours ? garageCloseHour : undefined,
        streetNumber: address!.streetNumber,
        streetName: address!.streetName,
        addressLine2: address!.addressLine2,
        county: address!.county,
        country: address!.country,
      });

      Toast.show({
        text1: 'Location created',
        type: 'success',
      });

      clearByPrefix(draftPrefix);

      await queryClient.invalidateQueries({
        queryKey: ['paginated/locations'],
      });

      router.navigate('/partner/more/locations');
    } catch (error: any) {
      console.error('Error creating:', error);
    }
  };

  const content = (
    <View style={{ flexGrow: 1, gap: 64 }}>
      <View style={styles.nav}>
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={() => {
            router.navigate('/partner/more/locations');
          }}
        />

        <Text type="title1">Add Location</Text>
      </View>

      <View
        style={{
          flexGrow: 1,
          justifyContent: 'space-between',
          paddingBottom: 24,
        }}
      >
        <View style={{ gap: 24, flexGrow: 1 }}>
          <View
            style={[
              {
                flexGrow: showGoogleAutoComplete ? 1 : 0,
                zIndex: showGoogleAutoComplete ? 3 : 1,
              },
              styles.addressContainer,
            ]}
          >
            {!showGoogleAutoComplete ? (
              <Input
                placeholder={
                  address ? address.description : 'Search and add an address'
                }
                onChangeText={() => {}}
                onFocus={() => {
                  setShowGoogleAutoComplete(true);
                }}
                leftIcon={
                  <Icon
                    type="naked"
                    icon={<Search />}
                    fillColor={theme.colors.icon.color}
                    width={22}
                    height={22}
                  />
                }
              />
            ) : null}

            {showGoogleAutoComplete ? (
              <GoogleAutocomplete
                onPress={(d: Address) => {
                  setAddress({
                    addressComponents: d.addressComponents,
                    description: d.description,
                    placeId: d.placeId,
                    latitude: d.latitude,
                    longitude: d.longitude,
                    city: d.city,
                    state: d.state,
                    zipcode: d.zipcode,
                    streetNumber: d.streetNumber,
                    streetName: d.streetName,
                    addressLine2: d.addressLine2,
                    county: d.county,
                    country: d.country,
                  });

                  setShowGoogleAutoComplete(false);
                }}
                style={{ width: '100%' }}
                autoFocus
              />
            ) : null}
          </View>

          <View
            style={[
              styles.inputsContainer,
              {
                zIndex: 2,
              },
            ]}
          >
            <InputMulti
              label="Pickup Instructions"
              text={pickupInstructions || ''}
              onChangeText={(s: string) => setPickupInstructions(s)}
              placeholder="Example: “Car is behind the building, use alley on the right”"
              style={{ minHeight: 100 }}
              tooltipText="Provide 1-2 sentences with specific pickup tips."
            />

            <InputMulti
              label="Dropoff Instructions"
              text={dropoffInstructions || ''}
              onChangeText={(s: string) => setDropoffInstructions(s)}
              placeholder="Example: “Plug in the car, park in the same spot, leave keys inside"
              style={{ minHeight: 100 }}
              tooltipText="Provide 1-2 sentences with specific return steps."
            />

            <Card>
              <View
                style={{
                  padding: 16,
                  borderBottomWidth: enableGarageHours ? 1 : 0,
                  borderBottomColor: theme.colors.card.default.borderColor,
                }}
              >
                <CheckItem
                  text="Garage hours"
                  caption="Set hours when the car can be picked up from this location. Use this for parking garages with limited access hours."
                  onPress={() => {
                    setEnableGarageHours(!enableGarageHours);
                  }}
                  active={enableGarageHours}
                  type="checkbox"
                />
              </View>

              {enableGarageHours ? (
                <View
                  style={{
                    padding: 16,
                    gap: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Dropdown
                    mode="single"
                    label="Available From"
                    options={options}
                    placeHolderText="Select Time"
                    onSelect={(selected: DropdownOption[]) => {
                      setGarageOpenHour(selected[0].value);
                    }}
                    values={garageOpenHour ? [garageOpenHour] : []}
                    style={{ flex: 1 }}
                  />

                  <Icon
                    type="naked"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.icon.color}
                    width={12}
                    height={12}
                  />

                  <Dropdown
                    mode="single"
                    label="Available Until"
                    options={options}
                    placeHolderText="Select Time"
                    onSelect={(selected: DropdownOption[]) => {
                      setGarageCloseHour(selected[0].value);
                    }}
                    values={garageCloseHour ? [garageCloseHour] : []}
                    style={{ flex: 1 }}
                  />
                </View>
              ) : null}
            </Card>
          </View>

          <View style={styles.buttonContainerDesktop}>
            <Button
              text="Save"
              type="primary"
              onPress={handleAddLocation}
              disabled={!pickupInstructions || !dropoffInstructions || !address}
              style={{ width: 100 }}
            />
          </View>
        </View>
      </View>
    </View>
  );

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
      }}
    >
      <KeyboardAwareScrollView
        style={styles.scrollView}
        contentContainerStyle={{
          paddingBottom: 100,
        }}
        keyboardShouldPersistTaps="always"
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS === 'web' ? (
          content
        ) : (
          <TouchableWithoutFeedback
            onPress={() => {
              Keyboard.dismiss();
              setShowGoogleAutoComplete(false);
            }}
            accessible={false}
          >
            {content}
          </TouchableWithoutFeedback>
        )}
      </KeyboardAwareScrollView>

      <View style={styles.buttonsContainerMobile}>
        <Button
          text="Save"
          type="primary"
          onPress={handleAddLocation}
          disabled={!pickupInstructions || !dropoffInstructions || !address}
        />

        <Button
          text="Cancel"
          type="ghost"
          onPress={() => {
            router.navigate('/partner/more/locations');
          }}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  scrollView: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
  addressContainer: {
    width: {
      md: 700,
    },
  },
  inputsContainer: {
    gap: 32,
    width: {
      md: 700,
    },
  },
  buttonsContainerMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    padding: 16,
  },
  buttonContainerDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
}));
